import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { LogoMark } from '../components/Logo';
import { isPlatformAdminUser } from '../lib/platformAdmin';

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ organization_name: '', full_name: '', email: '', password: '' });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 2);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const user = await register(form);
      nav(isPlatformAdminUser(user) ? '/internal' : '/dashboard');
    } catch (e) {
      setErr(e?.response?.data?.error || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col pt-16">
      <header className={`fixed inset-x-0 top-0 z-50 border-b idemgo-home-header ${scrolled ? 'idemgo-home-header-scrolled' : ''}`}>
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2"><LogoMark size={25}/><span className="font-display text-lg font-bold">idemGO</span></Link>
          <Link to="/login" data-testid="signup-go-login" className="text-sm text-zinc-600 hover:text-black">Already have an account?</Link>
        </div>
      </header>
      <main className="flex-1 grid place-items-center px-6 py-16 idemgo-grid-bg">
        <form onSubmit={submit} data-testid="signup-form" className="w-full max-w-sm border border-zinc-200 bg-white p-8 rounded-sm">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Create account</p>
          <h1 className="font-display text-2xl font-bold mt-1">Create your workspace</h1>
          <p className="text-sm text-zinc-500 mt-1">Identity verification and document signing for modern businesses.</p>

          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-6 mb-1">Organization</label>
          <input data-testid="signup-org" required value={form.organization_name} onChange={setField('organization_name')}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black" placeholder="Acme Fintech"/>

          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Your name</label>
          <input data-testid="signup-name" value={form.full_name} onChange={setField('full_name')}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black" placeholder="Jane Doe"/>

          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Email</label>
          <input data-testid="signup-email" required type="email" value={form.email} onChange={setField('email')}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>

          <label className="block text-xs uppercase tracking-widest text-zinc-500 mt-4 mb-1">Password</label>
          <input data-testid="signup-password" required type="password" value={form.password} onChange={setField('password')}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>

          {err && <p data-testid="signup-error" className="text-sm text-red-700 mt-3">{err}</p>}

          <button data-testid="signup-submit" disabled={loading}
            className="w-full mt-6 bg-black text-white rounded-sm py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
            {loading ? 'Creating…' : 'Create account'}
          </button>
        </form>
      </main>
    </div>
  );
}
